import { MoonIcon, SunIcon } from "@chakra-ui/icons";
import {
  Avatar,
  Box,
  CloseButton,
  Collapse,
  Drawer,
  DrawerContent,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useColorMode,
  useColorModeValue,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { default as LogoNav, default as LogoNavWhite } from "assets/images/Hustle.png";
import React, { useCallback, useEffect, useState } from "react";
import { BsMegaphone, BsTicket } from "react-icons/bs";
import {
  FiBriefcase,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiChevronUp,
  FiCreditCard,
  FiFolder,
  FiMenu,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { IoRocketOutline } from "react-icons/io5";
import { MdOutlineCall, MdOutlinePermDataSetting, MdWeb, MdOutlineBarChart } from "react-icons/md";
import { PiTrafficSignalLight } from "react-icons/pi";
import { RiImageCircleAiLine } from "react-icons/ri";
import { SlSocialFacebook } from "react-icons/sl";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  useGetActiveAccountsListQuery,
  useGetActiveAccountsQuery,
} from "store/adAccounts/adAccountsApi";
import { setCallsData, setLandersData } from "store/aiHub/aiSlice";
import { useGetLanderProjectsQuery, useGetPurchaseNumberByEmailQuery } from "store/aiHub/externalAiApi";
import { api } from "store/api";
import { logout, setActiveUserAdAccounts, setUserActiveAdAccountsList } from "store/auth/authSlice";
import { useGetFbActiveOrDeactiveQuery } from "store/BmAccounts/bmAccountApi";
import {
  selectFbData,
  selectFbDataRefetched,
  setFbData,
  setRefetchFbData,
} from "store/BmAccounts/bmAccountSlice";
import {
  setAccessAllAdAccounts,
  setAfter,
  setBefore,
  setCurrentPage,
  setSelectedAdsetId,
  setSelectedAdsId,
  setSelectedCampaignId,
} from "store/campaigns/campaignsSlice";
import { useGetAllCompaniesQuery } from "store/companies/companiesApi";
import { setActiveCompanies } from "store/companies/companySlice";
import { useGetSubscriptionsQuery } from "store/Subscription/SubscriptionApi";
import { selectSubscription, setActiveSubscriptions } from "store/Subscription/SubscriptionSlice";
import FileUploadStatusWidget from "./FileUploadStatusWidget";
import { FaWhatsapp } from "react-icons/fa";

const adminLinkItems = [
  { name: "Users", icon: FiUser, path: "clients" },
  { name: "Companies", icon: FiBriefcase, path: "companies" },
  { name: "Ad Accounts", icon: SlSocialFacebook, path: "accounts" },
  { name: "Top Ups", icon: FiCreditCard, path: "topup" },
  { name: "Tickets", icon: BsTicket, path: "requests" },
  { name: "Dashboard", icon: FiBriefcase, path: "dashboard" },
  { name: "Adspresso", icon: IoRocketOutline, path: "adspresso-analytics" },
  { name: "Active Clients", icon: FiUsers, path: "activity-analytics" },
];



const clientLinkItems = [
  {
    section: "Adspresso Management", // New section for Adspresso
    items: [
      {
        name: "About",
        icon: IoIosInformationCircleOutline,
        path: "about-adsmanager",
      },
      { name: "Launchpad", icon: IoRocketOutline, path: "launchpad" },
      { name: "My Campaigns", icon: BsMegaphone, path: "campaigns" },
      { name: "My Drive", icon: FiFolder, path: "uploads" },
      { name: "Ad Rules", icon: MdOutlinePermDataSetting, path: "rules" },
      { name: "30% ROAS Boost Setup", icon: PiTrafficSignalLight, path: "signal-gateway" },
      { name: "Page Analytics", icon: MdOutlineBarChart, path: "page-analytics" },
    ],
  },
  {
    section: "AIHub", // New section for AI Hub 
    items: [
      { name: "Image & Video", icon: RiImageCircleAiLine, path: "generate" },
      { name: "Call Center", icon: MdOutlineCall, path: "call-center" },
      { name: "Lander", icon: MdWeb, path: "lander" },
      { name: "WhatsApp Bot", icon: FaWhatsapp, path: "whatsapp-bot" },
    ],
  },
  {
    section: "Rental Management", // New section for Rental Management
    items: [
      {
        name: "About",
        icon: IoIosInformationCircleOutline,
        path: "about-rent",
      },
      // { name: "Companies", icon: FiBriefcase, path: "companies" },
      { name: "Ad Accounts", icon: SlSocialFacebook, path: "accounts" },
      { name: "Top Ups", icon: FiCreditCard, path: "topup" },
      { name: "Tickets", icon: BsTicket, path: "requests" },
    ],
  },
];

export default function SidebarWithHeader({ noPadding, children }) {
  const location = useLocation();
  const slug = location.pathname.replace(/\//g, "");
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { colorMode, toggleColorMode } = useColorMode();
  const userInfo = useSelector((state) => state.auth?.userInfo);
  const email = userInfo?.email;
  const isFbDataRefetched = useSelector(selectFbDataRefetched);
  const [shouldPoll, setShouldPoll] = useState(false);

  const dispatch = useDispatch();

  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Getting value from localStorage, or default to false if not
    const savedState = localStorage.getItem("sidebarCollapsed");
    return savedState ? JSON.parse(savedState) : false;
  });

  const handleToggleSidebar = () => {
    setIsCollapsed((prevState) => {
      const newState = !prevState;
      // Save the new state to localStorage
      localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
      return newState;
    });
  };

  const { data: companies, refetch: refetchCompanies } = useGetAllCompaniesQuery("getCompanies", {
    skip:
      userInfo?.role !== "client" ||
      slug == "uploads" ||
      slug.includes("launchpad") ||
      slug == "campaigns" ||
      slug == "rules" ||
      slug == "about-adsmanager",
  });

  const { data: adAccounts, refetch: refetchAdaccounts } = useGetActiveAccountsQuery(
    { currentPage: 1, searchValue: "" },
    {
      skip:
        userInfo?.role !== "client" ||
        slug == "uploads" ||
        slug.includes("launchpad") ||
        slug == "campaigns" ||
        slug == "rules" ||
        slug == "about-adsmanager",
    }
  );

  const { data: activeAccountsData, refetch: refetchActiveAdaccounts } =
    useGetActiveAccountsListQuery(
      {},
      {
        skip:
          userInfo?.role !== "client" ||
          slug == "uploads" ||
          slug.includes("launchpad") ||
          slug == "campaigns" ||
          slug == "rules" ||
          slug == "about-adsmanager",
      }
    );

  const {
    data: fBData,
    refetch: refetchFbData,
    isLoading: isFbDataLoading,
  } = useGetFbActiveOrDeactiveQuery(
    {},
    {
      pollingInterval: shouldPoll ? 2000 : 0,
      skipPollingIfUnfocused: true,
    }
  );

  const { data: subscriptions, isLoading: isSubscriptionsLoading, isFetching } = useGetSubscriptionsQuery({},{skip:true});
  const { data: callsData } = useGetPurchaseNumberByEmailQuery(email ?? skipToken);
  const { data: landersData } = useGetLanderProjectsQuery(email ?? skipToken);

  useEffect(() => {
    // If token is present, start polling
    if (token && fBData?.status !== "active" && fBData?.status !== "deactive") {
      setShouldPoll(true);
    } else {
      // Stop polling if fBData.status is 'active' or token is not present
      setShouldPoll(false);
    }
  }, [token, fBData]);

  useEffect(() => {
    if (isFbDataRefetched) {
      refetchFbData();
      dispatch(setRefetchFbData(false));
    }
  }, [isFbDataRefetched]);

  useEffect(() => {
    if (fBData) {
      dispatch(setFbData(fBData));
      localStorage.setItem("fBData", JSON.stringify(fBData));
    }
  }, [fBData]);

  useEffect(() => {
    if (callsData) {
      dispatch(setCallsData(callsData));
    }
  }, [callsData]);

  useEffect(() => {
    if (landersData) {
      dispatch(setLandersData(landersData));
    }
  }, [landersData]);


  useEffect(() => {
    const storedFbData = localStorage.getItem("fBData");
    if (storedFbData) {
      const parsedFbData = JSON.parse(storedFbData);
      dispatch(setFbData(parsedFbData));
    }
  }, [dispatch]);

  useEffect(() => {
    if (
      (userInfo?.role === "client" || userInfo?.role === "admin" || userInfo?.role === "media") &&
      adAccounts?.data?.length
    ) {
      dispatch(setActiveUserAdAccounts(adAccounts?.data));
    }
  }, [adAccounts]);

  useEffect(() => {
    if (
      (userInfo?.role === "client") &&
      subscriptions?.data?.length
    ) {
      dispatch(setActiveSubscriptions(subscriptions?.data?.[0]));
    }
  }, [subscriptions]);

  useEffect(() => {
    if (
      (userInfo?.role === "client" || userInfo?.role === "admin" || userInfo?.role === "media") &&
      companies?.data?.length
    ) {
      dispatch(setActiveCompanies(companies?.data));
    }
  }, [companies]);

  useEffect(() => {
    if (
      (userInfo?.role === "client" || userInfo?.role === "admin" || userInfo?.role === "media") &&
      activeAccountsData?.data?.length
    ) {
      dispatch(setUserActiveAdAccountsList(activeAccountsData));
    }
  }, [activeAccountsData]);

  // useEffect(() => {
  //     if ((userInfo?.role === 'client' || userInfo?.role === 'admin' || userInfo?.role === 'media') && AdAccountData?.data?.length) {
  //         dispatch(setSelectedAdAccounts(AdAccountData?.data))
  //     }
  // }, [AdAccountData])

  useEffect(() => {
    if (
      userInfo?.role === "client" &&
      slug == "requests" &&
      slug == "accounts" &&
      slug == "topup" &&
      slug == "companies"
    ) {
      if (!companies?.data?.length) {
        refetchCompanies(); // Only refetch if companies are not available
      }
      if (!adAccounts?.data?.length) {
        refetchAdaccounts(); // Only refetch if adAccounts are not available
      }
      if (!activeAccountsData?.data?.length) {
        refetchActiveAdaccounts(); // Only refetch if activeAdaccounts are not available
      }
    }
  }, [userInfo, adAccounts, companies, activeAccountsData]);

  const handleBusinessAccountUpdate = (updatedAccount) => {
    // if(updatedAccount){
    //     refetchAdaccountData();
    // }
  };

  return (
    <Box minH="100vh" bg={useColorModeValue("white", "#000023")}>
      <FileUploadStatusWidget />
      <SidebarContent
        onClose={() => onClose}
        display={{ base: "none", md: "block" }}
        onToggleSidebar={handleToggleSidebar}
        isCollapsed={isCollapsed}
        // businessAccountData={businessAccountData}
        onBusinessAccountChange={handleBusinessAccountUpdate}
      // isBusinessLoading={isBusinessLoading}
      />
      <Drawer
        autoFocus={false}
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        returnFocusOnClose={false}
        onOverlayClick={onClose}
        size="full"
      >
        <DrawerContent>
          <SidebarContent onClose={onClose} />
        </DrawerContent>
      </Drawer>
      {/* MOBILE NAV */}
      <MobileNav onOpen={onOpen} isCollapsed={isCollapsed} />
      <Box
        ml={{ base: 0, md: isCollapsed ? "80px" : "225px" }}
        p={{ base: noPadding ? "0px" : "20px", md: noPadding ? "0px" : "20px" }}
      >
        {children}
      </Box>
      {/* 
            <IconButton position={'fixed'} zIndex={3} bottom={[0, '10px']} left={[0, '20px']} w={3} mx={'auto'} mt={8} mb={2} icon={colorMode == 'dark' ? <SunIcon /> : <MoonIcon />} onClick={toggleColorMode} />
        */}
    </Box>
  );
}

const SidebarContent = ({
  onClose,
  onToggleSidebar,
  businessAccountData = [],
  onBusinessAccountChange,
  isCollapsed,
  ...rest
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const companies = useSelector((state) => state.company?.activeCompanies);
  const slug = location.pathname.replace(/\//g, "");
  const fbDeactive = useSelector(selectFbData);
  const userInfo = useSelector((state) => state.auth?.userInfo);
  const { colorMode, toggleColorMode } = useColorMode();
  // Replace these constants near the top of SidebarContent component
  // Replace these constants near the top of SidebarContent component
  const darkHeadingColor = useColorModeValue("brand.900", "brand.300"); // heading color (light/dark)
  const bgColor = useColorModeValue("transparent", "purple.500"); // sidebar group bg (light/dark)
  const mainMenuBorderColor = useColorModeValue("brand.200", "brand.700"); // border on collapse sections
  const iconColor = useColorModeValue("brand.800", "brand.300"); // icon + section title
  const borderColor = useColorModeValue("gray.400", "purple.400");

  const [isAdspressoCollapsed, setIsAdspressoCollapsed] = useState(() => {
    const savedState = localStorage.getItem("isAdspressoCollapsed");
    return savedState ? JSON.parse(savedState) : false; // Default to collapsed
  });
  const [isAiHubCollapsed, setIsAiHubCollapsed] = useState(() => {
    const savedState = localStorage.getItem("isAiHubCollapsed");
    return savedState ? JSON.parse(savedState) : true; // Default to collapsed
  });
  const [isRentalCollapsed, setIsRentalCollapsed] = useState(() => {
    const savedState = localStorage.getItem("isRentalCollapsed");
    return savedState ? JSON.parse(savedState) : true; // Default to collapsed
  });

  // Using useCallback to avoid unnecessary re-renders
  const handleAdspressoCollapseToggle = useCallback(() => {
    setIsAdspressoCollapsed((prev) => {
      const newState = !prev;
      localStorage.setItem("isAdspressoCollapsed", JSON.stringify(newState)); // Save to localStorage
      return newState;
    });
  }, []);

  const handleAiHubCollapseToggle = useCallback(() => {
    setIsAiHubCollapsed((prev) => {
      const newState = !prev;
      localStorage.setItem("isAiHubCollapsed", JSON.stringify(newState)); // Save to localStorage
      return newState;
    });
  }, []);

  const handleRentalCollapseToggle = useCallback(() => {
    setIsRentalCollapsed((prev) => {
      const newState = !prev;
      localStorage.setItem("isRentalCollapsed", JSON.stringify(newState)); // Save to localStorage
      return newState;
    });
  }, []);

  return (
    <>
      <Box
        transition="0.3s ease"
        bg={useColorModeValue("white", "#000023")}
        borderRight="1px"
        borderRightColor={useColorModeValue("gray.200", "purple.800")}
        //w={{ base: 'full', md: 60 }}
        pos="fixed"
        h="full"
        zIndex={2}
        {...rest}
      >

        <Flex
          h="20"
          alignItems="center"
          mx={isCollapsed ? "auto" : "8"}
          mb={"20px"}
          justifyContent={isCollapsed ? "center" : "space-between"}
        >
          <Link href="/launchpad">
            <Image
              w={isCollapsed ? "60px" : "160px"}
              src={colorMode === "dark" ? LogoNavWhite : LogoNav}
              alt="midas logo"
            />
          </Link>

          <IconButton
            aria-label="Toggle Sidebar"
            icon={isCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
            onClick={onToggleSidebar}
            variant="outline"
            display={{ base: "none", md: "flex" }}
            size="sm"
            _hover={{
              bg: colorMode === "light" ? "gray.50" : "gray.500",
            }}
            sx={{
              position: "absolute",
              right: "-16px",
              top: "60px",
              background: colorMode === "light" ? "white" : "#000023",
              zIndex: 10000,
            }}
          />

          {/* Show only on mobile */}
          <CloseButton pl={10} display={{ base: "flex", md: "none" }} onClick={onClose} />
        </Flex>
        {userInfo && (
          <>
            {(userInfo?.role === "admin" || userInfo?.role === "media" || userInfo?.role === "finance")
              ? adminLinkItems
                .filter(({ path }) => {
                  if (userInfo?.role === "media" || userInfo?.role === "finance") {
                    // Exclude all 3 specific paths for media/finance role
                    return !["dashboard", "adspresso-analytics", "activity-analytics"].includes(path);
                  }
                  return true; // Keep all for admin 
                })
                .map(({ name, icon, path }) => (
                  <NavItem
                    key={`${name}-${path}`}
                    icon={icon}
                    active={path === slug}
                    path={path}
                    isCollapsed={isCollapsed}
                  >
                    {!isCollapsed && name}
                  </NavItem>
                ))
              : null}


            {userInfo?.role == "client" ? (
              <>
                {/* {isOnboarded ? ( */}
                <>
                  {/* {hasActiveAdAccount ? ( */}
                  <Box overflowY="auto" maxHeight="85vh">
                    {/* Adspresso Management Section */}
                    <Box>
                      <Flex
                        onClick={handleAdspressoCollapseToggle}
                        cursor="pointer"
                        mx={2}
                        mb={1}
                        align="center"
                        justify="space-between"
                        p={"10px"}
                        bg={isAdspressoCollapsed ? "transparent" : bgColor}
                        borderRadius="lg"
                        border={`1px solid ${mainMenuBorderColor}`}
                        gap={1}
                      >
                        <Text fontWeight="bold" fontSize="14px" color={iconColor} pl={2}>
                          {isCollapsed ? "AM" : "Ads Manager"}
                        </Text>
                        {isAdspressoCollapsed ? (
                          <Icon as={FiChevronDown} boxSize={4} color={iconColor} />
                        ) : (
                          <Icon as={FiChevronUp} boxSize={4} color={iconColor} />
                        )}
                      </Flex>
                      <Collapse in={!isAdspressoCollapsed}>
                        {clientLinkItems
                          .find((item) => item.section === "Adspresso Management")
                          ?.items.map(({ name, icon, path }, index) => (
                            <React.Fragment key={`${name}-${path}-${index}`}>
                              {isCollapsed ? (
                                <Tooltip label={name} fontSize="xs" placement={"right"}>
                                  <Box>
                                    <NavItem
                                      key={`${name}-${path}-${index}`}
                                      icon={icon}
                                      active={path === slug}
                                      path={`${path}`}
                                      isCollapsed={isCollapsed}
                                      isDisabled={
                                        fbDeactive?.status === "deactive" &&
                                        path !== "about-adsmanager" &&
                                        path !== "launchpad"
                                      }
                                    >
                                      {name}
                                    </NavItem>
                                  </Box>
                                </Tooltip>
                              ) : (
                                <NavItem
                                  key={`${name}-${path}-${index}`}
                                  icon={icon}
                                  active={path === slug}
                                  path={`${path}`}
                                  isCollapsed={isCollapsed}
                                  fontSize={"14px"}
                                  isDisabled={
                                    fbDeactive?.status === "deactive" &&
                                    path !== "about-adsmanager" &&
                                    path !== "launchpad"
                                  }
                                >
                                  {name}
                                </NavItem>
                              )}
                            </React.Fragment>
                          ))}
                      </Collapse>
                    </Box>

                    {/* Ai Hub Section */}
                    <Box>
                      <Flex
                        onClick={handleAiHubCollapseToggle}
                        cursor="pointer"
                        mx={2}
                        mb={1}
                        align="center"
                        justify="space-between"
                        p={"10px"}
                        bg={isAiHubCollapsed ? "transparent" : bgColor}
                        borderRadius="lg"
                        border={`1px solid ${mainMenuBorderColor}`}
                        gap={1}
                      >
                        <Text fontWeight="bold" fontSize="14px" color={iconColor} pl={2}>
                          {isCollapsed ? "AH" : "AI Hub"}
                        </Text>
                        {isAiHubCollapsed ? (
                          <Icon as={FiChevronDown} boxSize={4} color={iconColor} />
                        ) : (
                          <Icon as={FiChevronUp} boxSize={4} color={iconColor} />
                        )}
                      </Flex>
                      <Collapse in={!isAiHubCollapsed}>
                        {clientLinkItems
                          .find((item) => item.section === "AIHub")
                          ?.items.map(({ name, icon, path }, index) => {
                            return (
                              <React.Fragment key={`${name}-${path}-${index}`}>
                                {isCollapsed ? (
                                  <Tooltip label={name} fontSize="xs" placement={"right"}>
                                    <Box>
                                      <NavItem
                                        key={`${name}-${path}-${index}`}
                                        icon={icon}
                                        active={path === slug}
                                        path={`${path}`}
                                        isCollapsed={isCollapsed}
                                      >
                                        {name}
                                      </NavItem>
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  <NavItem
                                    key={`${name}-${path}-${index}`}
                                    icon={icon}
                                    active={path === slug}
                                    path={`${path}`}
                                    isCollapsed={isCollapsed}
                                    fontSize={"14px"}
                                  >
                                    {name}
                                  </NavItem>
                                )}
                              </React.Fragment>
                            )
                          })}
                      </Collapse>
                    </Box>
                    {isCollapsed ? (
                      <Tooltip label={'Pricing'} fontSize="xs" placement={"right"}>
                        <Flex
                          cursor="pointer"
                          mb={1}
                          mx={2}
                          align="center"
                          justify="space-between"
                          p={"10px"}
                          borderRadius="lg"
                        >
                          <Link as={RouterLink} to="/pricing" style={{ textDecoration: 'none', width: '100%' }}>
                            <Text fontWeight="bold" fontSize="14px" color={iconColor} pl={2}>
                              {'P'}
                            </Text>
                          </Link>
                        </Flex>
                      </Tooltip>
                    ) : (
                      <Box>
                        <NavItem
                          key={"pricing"}
                          icon={""}
                          active={slug === "pricing"}
                          path={"pricing"}
                          isCollapsed={isCollapsed}
                        >
                          Pricing
                        </NavItem>
                      </Box>
                    )}

                    {/* Rental Management Section */}
                    <Box>
                      <Flex
                        onClick={handleRentalCollapseToggle}
                        cursor="pointer"
                        mb={1}
                        mx={2}
                        align="center"
                        justify="space-between"
                        p={"10px"}
                        bg={isRentalCollapsed ? "transparent" : bgColor}
                        borderRadius="lg"
                        border={`1px solid ${mainMenuBorderColor}`}
                      >
                        <Text fontWeight="bold" fontSize="14px" color={iconColor} pl={2}>
                          {isCollapsed ? "RA" : "Rent Ad Account "}
                        </Text>

                        {isRentalCollapsed ? (
                          <Icon as={FiChevronDown} boxSize={4} color={iconColor} />
                        ) : (
                          <Icon as={FiChevronUp} boxSize={4} color={iconColor} />
                        )}
                      </Flex>
                      <Collapse in={!isRentalCollapsed}>
                        {clientLinkItems
                          .find((item) => item.section === "Rental Management")
                          ?.items.map(({ name, icon, path }, index) => (
                            <React.Fragment key={`${name}-${path}-${index}`}>
                              {isCollapsed ? (
                                <Tooltip label={name} fontSize="xs" placement={"right"}>
                                  <Box>
                                    <NavItem
                                      key={`${name}-${path}-${index}`}
                                      icon={icon}
                                      active={path === slug}
                                      path={path}
                                      isCollapsed={isCollapsed}
                                      isDisabled={path === "topup" && companies?.length === 0}
                                    >
                                      {name}
                                    </NavItem>
                                  </Box>
                                </Tooltip>
                              ) : (
                                <NavItem
                                  key={`${name}-${path}-${index}`}
                                  icon={icon}
                                  active={path === slug}
                                  path={path}
                                  isCollapsed={isCollapsed}
                                  fontSize={"14px"}
                                  isDisabled={path === "topup" && companies?.length === 0}
                                >
                                  {name}
                                </NavItem>
                              )}
                            </React.Fragment>
                          ))}
                      </Collapse>
                    </Box>

                    <Box pt={0}>
                      {isCollapsed ? (
                        <Tooltip label={'Companies'} fontSize="xs" placement={"right"}>
                          <Flex
                            cursor="pointer"
                            mb={1}
                            mx={2}
                            align="center"
                            justify="space-between"
                            p={"10px"}
                            borderRadius="lg"
                          >
                            <Link as={RouterLink} to="/companies" style={{ textDecoration: 'none', width: '100%' }}>
                              <Text fontWeight="bold" fontSize="14px" color={iconColor} pl={2}>
                                {'C'}
                              </Text>
                            </Link>
                          </Flex>
                        </Tooltip>
                      ) : (
                        <NavItem
                          key={"companies"}
                          icon={""}
                          active={slug === "companies"}
                          path={"companies"}
                          isCollapsed={isCollapsed}
                        >
                          Companies
                        </NavItem>
                      )}
                      <NavItem
                        key={"payment"}
                        icon={""}
                        active={slug === "payment"}
                        path={"payment"}
                        isCollapsed={isCollapsed}
                        isDisabled
                      >
                        Payments
                      </NavItem>
                    </Box>
                  </Box>
                  {/* ) : (
                    <>
                        {clientLinkItems.filter(({ name }) => name !== 'Top Ups').map(({ name, icon, path }, index) => (
                            <NavItem key={`${name}-${path}-${index}`} icon={icon} active={path === slug} path={path} isCollapsed={isCollapsed}>
                                {!isCollapsed && name}
                            </NavItem>
                        ))}
                    </>
                )} */}
                </>
                {/* ) : (
                  <NavItem key={'companies'} icon={FiBriefcase} active={true} path={'companies'} isCollapsed={isCollapsed}>
                      Companies
                  </NavItem>
                  null
              )} */}
              </>
            ) : null}
          </>
        )}
      </Box>
    </>
  );
};

const NavItem = ({ icon, path, active, children, isCollapsed, isDisabled, ...rest }) => {
  const dispatch = useDispatch();

  const hoverBackground = useColorModeValue("purple.200", "brand.800");
  const hoverIconColor = useColorModeValue("brand.900", "brand.300");
  const navBackground = useColorModeValue("purple.500", "brand.500");
  const navTextColor = useColorModeValue("brand.900", "brand.500");
  const activeTextColor = useColorModeValue("white", "brand.900");
  const iconColor = useColorModeValue("brand.800", "brand.300");
  const bgColor = useColorModeValue("transparent", "purple.500");
  const userInfo = useSelector((state) => state.auth?.userInfo);

  const handleClick = (e) => {
    if (isDisabled) {
      e.preventDefault();
      return;
    }

    dispatch(setSelectedCampaignId(null));
    dispatch(setSelectedAdsetId(null));
    dispatch(setSelectedAdsId(null));
    dispatch(setAfter(""));
    dispatch(setBefore(""));
    dispatch(setCurrentPage(0));
    dispatch(setAccessAllAdAccounts(false));
    localStorage.removeItem("cachedCampaign");
    localStorage.removeItem("after");
    localStorage.removeItem("before");
    localStorage.removeItem("selectedAdsAccount");
    localStorage.removeItem("sourcePublish");
  };

  return (
    <Link
      as={RouterLink}
      to={`/${path}`}
      style={{ textDecoration: "none" }}
      _focus={{ boxShadow: "none" }}
      onClick={handleClick}
    >
      <Flex
        align="center"
        p="10px"
        mx="4"
        borderRadius="lg"
        role="group"
        cursor={isDisabled ? "not-allowed" : "pointer"}
        bg={active ? navBackground : ""}
        color={active ? "white" : hoverIconColor}
        _hover={{
          bg: !active && !isDisabled ? hoverBackground : "",
        }}
        opacity={isDisabled ? 0.5 : 1}
        {...rest}
      >
        {icon && (
          <Icon
            mr={isCollapsed ? "0" : "4"}
            fontSize="14"
            color={active ? activeTextColor : navTextColor}
            _groupHover={{
              color: !active && !isDisabled ? hoverIconColor : "",
            }}
            as={icon}
          />
        )}
        {!isCollapsed && (
          <Box>
            <Text
              fontWeight={
                (path === "companies" && userInfo?.role === "client") ||
                  ["payment", "pricing"].includes(path)
                  ? "bold"
                  : "medium"
              }
              fontSize="sm"
              color={active ? activeTextColor : ["companies", "payment", "pricing"].includes(path) ? iconColor : navTextColor}
            >
              {children}
            </Text>
            {isDisabled && ["payment"].includes(path) && (
              <Text fontSize="10px">
                Coming soon
              </Text>
            )}
          </Box>
        )}
      </Flex>
    </Link>
  );
};

const MobileNav = ({ onOpen, isCollapsed, ...rest }) => {
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state?.auth?.userInfo);
  const subscriptionInfo = useSelector(selectSubscription);
  const dispatch = useDispatch();
  const { colorMode, toggleColorMode } = useColorMode();

  return (
    <Flex
      ml={{ base: 0 }}
      px={{ base: 4, md: 4 }}
      w="full"
      height="20"
      alignItems="center"
      bg={useColorModeValue("background.light", "background.dark")}
      borderBottomWidth="1px"
      borderBottomColor={useColorModeValue("brand.100", "brand.800")}
      justifyContent={{ base: "space-between", md: "flex-end" }}
      {...rest}
    >
      <IconButton
        display={{ base: "flex", md: "none" }}
        onClick={onOpen}
        variant="outline"
        aria-label="open menu"
        icon={<FiMenu />}
      />

      <Link href="/" display={{ base: "flex", md: "none" }}>
        <Image w="200px" src={colorMode === "dark" ? LogoNavWhite : LogoNav} alt="midas logo" />
      </Link>

      <HStack spacing={{ base: 0, md: 6 }}>
        <Flex alignItems="center">
          <IconButton
            variant="outline"
            aria-label="toggle color mode"
            icon={colorMode === "dark" ? <SunIcon /> : <MoonIcon />}
            onClick={toggleColorMode}
            size="sm"
            _hover={{
              bg: useColorModeValue("brand.50", "brand.800"),
            }}
            _active={{
              bg: useColorModeValue("brand.100", "brand.700"),
            }}
            _focus={{ boxShadow: "none" }}
            mr="10px"
            borderRadius="full"
            border="none"
          />

          <Menu>
            <MenuButton py={2} transition="all 0.3s" _focus={{ boxShadow: "none" }}>
              <HStack>
                <Avatar name={`${userInfo?.firstName} ${userInfo?.lastName}`} size="sm" />
                <VStack
                  display={{ base: "none", md: "flex" }}
                  alignItems="flex-start"
                  spacing="1px"
                  ml="2"
                >
                  <Text fontSize="sm" textTransform="capitalize">
                    {userInfo?.firstName} {userInfo?.lastName}
                  </Text>
                  <Text
                    fontSize="xs"
                    color={useColorModeValue("text.muted.light", "text.muted.dark")}
                    textTransform="capitalize"
                  >
                    {subscriptionInfo?.planRef?.name ? `${subscriptionInfo?.planRef?.name} plan` : userInfo?.role}
                  </Text>
                </VStack>
                <Box display={{ base: "none", md: "flex" }}>
                  <FiChevronDown />
                </Box>
              </HStack>
            </MenuButton>
            <MenuList>
            {["client"].includes(userInfo?.role) && (
              <>
                {[
                  { label: "Subscription", path: "/subscription" },
                  // { label: "Buy credits", path: "/buy-credits" },
                  { label: "Plan usage", path: "/plan-usage" },
                ].map((item) => (
                  <MenuItem key={item.path} onClick={() => navigate(item.path)}>
                    {item.label}
                  </MenuItem>
                ))}
              </>
            )}

              {/* <MenuItem onClick={() => navigate("/subscription")}>Subscription</MenuItem> */}

              {["admin", "client"].includes(userInfo?.role) && (
                <MenuItem onClick={() => navigate("/logs")}>Logs</MenuItem>
              )}
              <MenuItem onClick={() => navigate("/change-password")}>Change password</MenuItem>
              <MenuDivider />
              <MenuItem
                onClick={() => {
                  // Dispatch logout and reset API
                  dispatch(logout());
                  dispatch(api.util.resetApiState());

                  // Clear localStorage keys
                  localStorage.removeItem('currentPage');
                  localStorage.removeItem('fBData');
                  localStorage.removeItem('isAdspressoCollapsed');
                  localStorage.removeItem('isRentalCollapsed');
                  localStorage.removeItem('isAiHubCollapsed');
                  localStorage.removeItem('selectedAdset');
                  localStorage.removeItem('sidebarCollapsed');

                  // Navigate to login
                  navigate("/signIn");
                }}
              >
                Sign out
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </HStack>
    </Flex>
  );
};
